import React from 'react';

type Breakpoint = 'desktop' | 'tablet' | 'mobile';

type GridRect = {
    x: number;
    y: number;
    width: number;
    height: number;
};

type ItemLayout = Partial<Record<Breakpoint, GridRect>>;

type BoardItem = {
    id: string;
    content: React.ReactNode;
    layout: ItemLayout;
};

type ResponsiveGridBoardProps = {
    items: BoardItem[];
    columns?: number;
    rowHeight?: number;
    gap?: number;
    breakpointWidths?: {
        desktop: number;
        tablet: number
    };
    className?: string;
    style?: React.CSSProperties;
};

const useBreakpoint = (bw: {
    desktop: number;
    tablet: number
}): Breakpoint => {
    const get = () => {
        const w = typeof window !== 'undefined' ? window.innerWidth : bw.desktop;
        if (w >= bw.desktop) {
            return 'desktop';
        }
        if (w >= bw.tablet) {
            return 'tablet';
        }
        return 'mobile';
    };
    const [ bp, setBp ] = React.useState<Breakpoint>(get());
    React.useEffect(() => {
        const onResize = () => setBp(get());
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ bw.desktop, bw.tablet ]);
    return bp;
};

const pickLayout = (il: ItemLayout, bp: Breakpoint): GridRect | undefined => {
    if (bp === 'desktop') {
        return il.desktop ?? il.tablet ?? il.mobile;
    }
    if (bp === 'tablet') {
        return il.tablet ?? il.desktop ?? il.mobile;
    }
    return il.mobile ?? il.tablet ?? il.desktop;
};

const clampRect = (r: GridRect, columns: number): GridRect => {
    const x = Math.max(1, Math.min(columns, Math.floor(r.x)));
    const width = Math.max(1, Math.min(columns - x + 1, Math.floor(r.width)));
    const y = Math.max(1, Math.floor(r.y));
    const height = Math.max(1, Math.floor(r.height));
    return { x, y, width, height };
};

const computeTotalRows = (rects: GridRect[]) => {
    let max = 0;
    for (const r of rects) {
        const bottom = r.y + r.height - 1;
        if (bottom > max) {
            max = bottom;
        }
    }
    return Math.max(0, max);
};

const GridBoard: React.FC<ResponsiveGridBoardProps> = ({
    items,
    columns = 12,
    rowHeight = 100,
    gap = 8,
    breakpointWidths = { desktop: 1200, tablet: 768 },
    className,
    style,
}) => {
    const bp = useBreakpoint(breakpointWidths);

   const placed = React.useMemo(() => {
    let localColumns = columns;

    const isMashup837 = window.location.pathname.includes('mashups/837');
    const screenWidth = window.innerWidth;

    if (isMashup837 && screenWidth < 1840 && screenWidth >= 1740){
        localColumns = 13;
    }
    if (isMashup837 && screenWidth < 1740 && screenWidth >= 1600){
        localColumns = 12;
    }
    if (isMashup837 && screenWidth < 1600 && screenWidth >= 1475){
        localColumns = 11;
    }
    if (isMashup837 && screenWidth < 1475){
        localColumns = 10;
    }

    const mapped = items
        .map(it => {
            const raw = pickLayout(it.layout, bp);
            if (!raw) return null;

            const rect = clampRect(raw, localColumns);
            return { id: it.id, content: it.content, rect };
        })
        .filter(Boolean) as {
            id: string;
            content: React.ReactNode;
            rect: GridRect
        }[];

    const totalRows = computeTotalRows(mapped.map(m => m.rect));

    return { mapped, totalRows };

}, [items, bp, columns]);

    return (
        <div
            className={className}
            style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${columns}, 1fr)`,
                gridAutoRows: `${rowHeight}px`,
                gridTemplateRows: placed.totalRows > 0 ? `repeat(${placed.totalRows}, ${rowHeight}px)` : undefined,
                gap,
                width: '100%',
                ...style,
            }}
        >
            {placed.mapped.map(({ id, content, rect }) => (
                <div
                    key={id}
                    style={{
                        gridColumn: `${rect.x} / span ${rect.width}`,
                        gridRow: `${rect.y} / span ${rect.height}`,
                        minWidth: 0,
                        minHeight: 0,
                    }}
                >
                    {content}
                </div>
            ))}
        </div>
    );
};

export default GridBoard;
